import { createContext, useContext, type ReactNode } from 'react';
import { useImportStatusQuery } from './client';
import { settingsPhase } from './settingsPhase';

export interface ImportStatusValue {
  /**
   * App-level "the user's blob has been imported into the service" flag, sourced
   * from the server-authoritative `_import` meta-domain. This single boolean is
   * the "trust the service" signal for every owned domain's read source,
   * replacing the old per-domain blob `migrated` flag.
   */
  imported: boolean;
  /** True while the status query is still resolving (phase ≥ 2 only). */
  isLoading: boolean;
}

// Default: not imported / not loading. An absent provider (popups, tests) is
// therefore treated as "read the blob", which is always the safe fallback.
const ImportStatusContext = createContext<ImportStatusValue>({
  imported: false,
  isLoading: false,
});

export const useImportStatus = (): ImportStatusValue =>
  useContext(ImportStatusContext);

/**
 * Exposes the app-level import status. The status query only runs when the
 * global phase is ≥ 2 (dual-write); below that the service is never read, so
 * `imported` is always `false` and no request is made.
 */
export function ImportStatusProvider({ children }: { children: ReactNode }) {
  const enabled = settingsPhase() >= 2;
  const { data, isLoading } = useImportStatusQuery({ enabled });

  // Derived during render — no effect, no duplicated state (Rule 1).
  const value: ImportStatusValue = {
    imported: enabled ? Boolean(data?.imported) : false,
    isLoading: enabled ? isLoading : false,
  };

  return (
    <ImportStatusContext.Provider value={value}>
      {children}
    </ImportStatusContext.Provider>
  );
}
